import type { Sfx } from "../audio.ts";
import {
  FRAME_WORLD,
  LANDMASSES,
  MODE_SWIM,
  MODE_WALK,
  OUTPOSTS,
  PLAYER,
  coastRadius,
  isTrodden,
  landmassAt,
  stairSpan,
  terrainHeight,
} from "@tiny-seas/shared";
import { gaitFor } from "../character/gait.ts";
import { RepairClock, repairStrikesBetween } from "../character/repair-swing.ts";

/**
 * Gameplay → sound decisions that do not touch Web Audio: what the feet are on,
 * when a stride/stroke/landing happens, and the cadence of held actions. Kept
 * pure so movement edge cases are testable without an audio graph.
 */

export type Surface = "wood" | "sand" | "grass" | "stone";

/** Pier boards are wood when the feet are within this height of the deck top. */
const PIER_TOLERANCE = 0.35;
/** Midpoints of the visible terrain colour blends in `Environment.buildLandmass`. */
const SAND_BELOW = 1.35;
const ROCK_ABOVE = 11;
const ROCK_SLOPE = 1.05;
/** Feet this far above the terrain stand on a structure (rock, building, landmark). */
const ON_STRUCTURE = 0.5;

/** Material under a body's feet. `x/y/z` are in the body's frame. */
export function surfaceAt(frame: number, x: number, y: number, z: number): Surface {
  if (frame !== FRAME_WORLD) return "wood";
  for (const o of OUTPOSTS) {
    const p = o.pier;
    if (Math.abs(y - p.top) > PIER_TOLERANCE) continue;
    if (x >= p.x0 && x <= p.x1 && z >= p.z0 && z <= p.z1) return "wood";
    for (const g of o.gangways) if (x >= g.minX && x <= g.maxX && z >= g.minZ && z <= g.maxZ) return "wood";
  }
  // Island stairs and jetties are timber; jetties reach past the landmass bounds.
  for (const l of LANDMASSES) {
    for (const s of l.stairs ?? []) {
      const span = stairSpan(l, s, 0, s.steps * s.tread + s.landing);
      if (x >= span.minX && x <= span.maxX && z >= span.minZ && z <= span.maxZ && y >= s.base - PIER_TOLERANCE && y <= s.top + PIER_TOLERANCE) return "wood";
    }
    for (const d of l.docks ?? []) {
      if (x >= l.cx + d.x0 && x <= l.cx + d.x1 && z >= l.cz + d.z0 && z <= l.cz + d.z1 && Math.abs(y - d.top) <= PIER_TOLERANCE) return "wood";
    }
  }
  const land = landmassAt(x, z);
  if (!land) return "stone";
  const h = terrainHeight(x, z);
  if (y - h > ON_STRUCTURE) return "stone";
  const slope = Math.abs(terrainHeight(x + 1, z) - h) + Math.abs(terrainHeight(x, z + 1) - h);
  // Cliff-walled plateaus stay grassy at any height, like their rendered terrain.
  if ((!land.mesas && h >= ROCK_ABOVE) || slope >= ROCK_SLOPE) return "stone";
  return h < SAND_BELOW || isTrodden(land, x - land.cx, z - land.cz) ? "sand" : "grass";
}

/** Width of the audible surf band around each coastline, metres. */
const SHORE_BAND = 45;

/** 0 (open sea or deep inland) … 1 (on the coastline), from the nearest coast. */
export function shoreProximity(x: number, z: number): number {
  let best = SHORE_BAND;
  for (const l of LANDMASSES) {
    const dx = x - l.cx;
    const dz = z - l.cz;
    const d = Math.sqrt(dx * dx + dz * dz);
    // Coast radii vary by well under 2× the nominal radius.
    if (d - l.radius * 2 > best) continue;
    const gap = Math.abs(d - coastRadius(l, Math.atan2(dz, dx)));
    if (gap < best) best = gap;
  }
  return Math.max(0, Math.min(1, 1 - best / SHORE_BAND));
}

/** One sampled body state for footstep/stroke/landing tracking. */
export interface MotionSample {
  frame: number;
  /** Ship generation for ship frames, -1 for the world frame. */
  generation: number;
  mode: number;
  x: number;
  y: number;
  z: number;
  grounded: boolean;
  /** Alive, connected and in a crew; false resets the tracker. */
  enabled: boolean;
  carrying: boolean;
  sliding: boolean;
  crouched: boolean;
  sprinting: boolean;
  station: string;
}

export interface MotionCues {
  steps: number;
  strokes: number;
  /** 0 when no landing; otherwise landing intensity 0.2 … 1. */
  landing: number;
}

export const STROKE = 1.8;
/** Fraction of a stride already "taken" when movement starts, so the first footfall comes early. */
const START_PHASE = 0.6;
/** Most cues of one kind a single update may emit. */
export const MAX_CUES = 2;
/** Falls slower than a drop of one step height are just walking down a ledge. */
const LANDING_MIN_FALL = Math.sqrt(2 * PLAYER.gravity * PLAYER.stepHeight);
const MOVING = 1e-4;
/** Stillness shorter than this (interpolation stalls, a tap of the key) keeps the stride phase. */
const PAUSE = 0.25;

/** Distance-driven cadence for one gait (walking or swimming). */
class Gait {
  private distance = 0;
  private still = PAUSE;

  reset(): void {
    this.distance = 0;
    this.still = PAUSE;
  }

  /** Cues due after moving `d` metres; `active` false (wrong mode/airborne/station) resets. */
  advance(active: boolean, d: number, dt: number, length: number): number {
    if (!active) {
      this.reset();
      return 0;
    }
    if (d <= MOVING) {
      this.still += dt;
      if (this.still >= PAUSE) this.distance = 0;
      return 0;
    }
    if (this.still >= PAUSE) this.distance = length * START_PHASE;
    this.still = 0;
    this.distance += d;
    const n = Math.min(MAX_CUES, Math.floor(this.distance / length));
    this.distance -= n * length;
    // Distance beyond the per-update cap is dropped, not owed.
    if (this.distance >= length) this.distance %= length;
    return n;
  }
}

/**
 * Converts physical movement into footfalls, swim strokes and landings. Distance
 * comes from accepted movement only, so frame rate, held keys against walls and
 * standing on a moving ship never fabricate strides.
 */
export class MovementAudioTracker {
  private initialised = false;
  private readonly walk = new Gait();
  private readonly swim = new Gait();
  private fall = 0;

  reset(): void {
    this.initialised = false;
    this.walk.reset();
    this.swim.reset();
    this.fall = 0;
  }

  advance(previous: Readonly<MotionSample>, current: Readonly<MotionSample>, dt: number, out: MotionCues): void {
    out.steps = 0;
    out.strokes = 0;
    out.landing = 0;
    if (!current.enabled || !previous.enabled || previous.frame !== current.frame || previous.generation !== current.generation || dt <= 0) {
      this.reset();
      return;
    }
    const d = Math.hypot(current.x - previous.x, current.z - previous.z);
    if (d > 8 * dt + 0.5) {
      this.reset();
      return;
    }
    if (!this.initialised) {
      this.initialised = true;
      return;
    }
    const free = current.station === "";
    // Landing: airborne → grounded after a real fall.
    if (!current.grounded) this.fall = Math.max(this.fall, (previous.y - current.y) / dt);
    else {
      if (!previous.grounded && free && current.mode === MODE_WALK && this.fall >= LANDING_MIN_FALL) {
        out.landing = Math.max(0.2, Math.min(1, this.fall / 8));
      }
      this.fall = 0;
    }
    const canWalk = free && current.grounded && current.mode === MODE_WALK && !current.sliding;
    out.steps = this.walk.advance(canWalk, d, dt, gaitFor(current.crouched, current.sprinting, current.carrying).step);
    out.strokes = this.swim.advance(free && current.mode === MODE_SWIM, d, dt, STROKE);
  }
}

export type HeldAction = "repair" | "fill";
const FILL_INTERVAL = 0.6;
export const ACTION_SFX = { repair: "repair", fill: "scoop" } as const satisfies Record<HeldAction, Sfx>;

/**
 * Cadence of a held repair or bucket fill. Repairs tick on each hammer strike (the shared
 * swing timing on the same smoothed progress clock the rigs draw); a fill ticks once on
 * entry, then every interval while it lasts.
 */
export class ActionAudioTracker {
  private action: HeldAction | null = null;
  private elapsed = 0;
  private readonly clock = new RepairClock();

  reset(): void {
    this.action = null;
    this.elapsed = 0;
    this.clock.advance(false, 0, 0);
  }

  advance(action: string, enabled: boolean, dt: number, progress: number): HeldAction | null {
    if (!enabled || (action !== "repair" && action !== "fill")) {
      this.reset();
      return null;
    }
    if (action === "repair") {
      const entering = this.action !== "repair", before = this.clock.value;
      this.action = action;
      const now = this.clock.advance(true, progress, dt);
      // Joining mid-repair is silent until the next blow; a long hitch lands one strike, never a burst.
      return !entering && repairStrikesBetween(before, now) > 0 ? action : null;
    }
    if (action !== this.action) {
      this.reset();
      this.action = action;
      return action;
    }
    this.elapsed += dt;
    if (this.elapsed < FILL_INTERVAL) return null;
    this.elapsed -= FILL_INTERVAL;
    // A long gap (hitch) yields one tick, never a catch-up burst.
    if (this.elapsed >= FILL_INTERVAL) this.elapsed = 0;
    return action;
  }
}
